import { errors, RoutesApiConst } from "../constants";
import {
  createErrorResult,
  createSuccessResult,
  type SimpleResult,
} from "../dto";
import { Api } from "../helpers/api/api";
import { apiBaseUrl } from "../helpers/env.helpers";
import { normalizeUrlBasePath } from "../helpers/url.helpers";
import type {
  IMcpConnectorActionDto,
  IMcpConnectorActionReviewDto,
  IOauthConsentRedirectDto,
  IOauthConsentRequestDto,
  IOauthGrantListItemDto,
  IOauthProtectedResourceMetadataDto,
  IOauthRevokeAllResultDto,
} from "../models";

// MCP connector OAuth endpoints (RYDE-7833): the SPA consent screen + connected-apps management.
// All endpoints are cookie-session authenticated and return the SimpleResult envelope.
export class OauthApi {
  static async getConsentRequest(
    requestId: string,
  ): Promise<SimpleResult<IOauthConsentRequestDto> | undefined> {
    const response = await Api.get<SimpleResult<IOauthConsentRequestDto>>(
      RoutesApiConst.oauth.consentRequest(requestId),
    );

    return response?.data;
  }

  static async approveConsent(
    requestId: string,
    companyId: number,
  ): Promise<SimpleResult<IOauthConsentRedirectDto> | undefined> {
    const response = await Api.post<SimpleResult<IOauthConsentRedirectDto>>(
      RoutesApiConst.oauth.consentApprove(requestId),
      { companyId },
    );

    return response?.data;
  }

  static async denyConsent(
    requestId: string,
  ): Promise<SimpleResult<IOauthConsentRedirectDto> | undefined> {
    const response = await Api.post<SimpleResult<IOauthConsentRedirectDto>>(
      RoutesApiConst.oauth.consentDeny(requestId),
    );

    return response?.data;
  }

  static async listGrants(
    companyId: number,
  ): Promise<SimpleResult<IOauthGrantListItemDto[]> | undefined> {
    const response = await Api.get<SimpleResult<IOauthGrantListItemDto[]>>(
      RoutesApiConst.oauth.grants(companyId),
    );

    return response?.data;
  }

  static async revokeGrant(
    companyId: number,
    grantId: string,
  ): Promise<SimpleResult<boolean> | undefined> {
    const response = await Api.delete<SimpleResult<boolean>>(
      RoutesApiConst.oauth.grant(companyId, grantId),
    );

    return response?.data;
  }

  /**
   * The MCP server's discovery document, which names its public connect page. It is a bare RFC
   * document, not the API envelope, so it is fetched plainly like the translation bundles; it is
   * public, so no credentials are sent.
   */
  static async getProtectedResourceMetadata(): Promise<
    SimpleResult<IOauthProtectedResourceMetadataDto>
  > {
    const url = `${normalizeUrlBasePath(apiBaseUrl() || "")}${RoutesApiConst.oauth.protectedResourceMetadata}`;

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        return createErrorResult(
          errors.common.invalidResponse,
          undefined,
          response.status,
        );
      }

      // A JSON document from our own API; the caller validates the one URL it uses.
      const metadata =
        (await response.json()) as IOauthProtectedResourceMetadataDto;

      return createSuccessResult(metadata);
    } catch (error) {
      return createErrorResult(errors.common.invalidResponse, undefined, error);
    }
  }

  /** Admin kill switch: revokes every live connector grant of the company. */
  static async adminRevokeAllGrants(
    companyId: number,
  ): Promise<SimpleResult<IOauthRevokeAllResultDto> | undefined> {
    const response = await Api.delete<SimpleResult<IOauthRevokeAllResultDto>>(
      RoutesApiConst.oauth.grants(companyId),
    );

    return response?.data;
  }

  /** An AI-connector action waiting for this user's approval (RYDE-8447). */
  static async getConnectorAction(
    actionId: string,
  ): Promise<SimpleResult<IMcpConnectorActionReviewDto> | undefined> {
    const response = await Api.get<SimpleResult<IMcpConnectorActionReviewDto>>(
      RoutesApiConst.oauth.connectorAction(actionId),
    );

    return response?.data;
  }

  static async approveConnectorAction(
    actionId: string,
  ): Promise<SimpleResult<IMcpConnectorActionDto> | undefined> {
    const response = await Api.post<SimpleResult<IMcpConnectorActionDto>>(
      RoutesApiConst.oauth.connectorActionApprove(actionId),
    );

    return response?.data;
  }

  static async denyConnectorAction(
    actionId: string,
  ): Promise<SimpleResult<IMcpConnectorActionDto> | undefined> {
    const response = await Api.post<SimpleResult<IMcpConnectorActionDto>>(
      RoutesApiConst.oauth.connectorActionDeny(actionId),
    );

    return response?.data;
  }
}
